// /opt/ptg01-v1/front-extranet/components/BottomTabBar.tsx
// Navigation mobile — visible < 1024px (lg:hidden).
// 5 items principaux + "Plus" qui ouvre un sheet avec les items secondaires.
// Safe-area inset iOS via env(safe-area-inset-bottom).
// EXT-L0 (2026-09-30) : libellés alignés sur la sidebar (« Compte d'activité », « Notes de frais »,
// « Messages », « Mon profil ») sur deux lignes au plus ; « Paramètres » (/parametres → 404) retiré.
// Les data-testid ne changent pas. Barre à 5 onglets + scanner central : lot L1.
'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
    LayoutDashboard, CalendarCheck, Wallet, Receipt,
    MoreHorizontal, FileText, Gift, UserCircle2,
    LogOut, X, Bell, LifeBuoy,
    Briefcase, BadgeEuro, ArrowLeftRight,
    type LucideIcon,
} from 'lucide-react';
import { fetchExtranet } from '../lib/fetchExtranet';
import { useAuthOptional } from '../lib/AuthContext';
import { dispatchNavSectionClick } from '../lib/navEvent';

interface TabItem { key: string; href: string; label: string; icon: LucideIcon }

// 5 tabs primaires (les actions les plus fréquentes) + Plus = 6 items au total.
// Missions accessible via le menu Plus.
const PRIMARY: TabItem[] = [
    { key: 'dashboard',       href: '/',                 label: 'Accueil',        icon: LayoutDashboard },
    { key: 'compte-activite', href: '/compte-activite',  label: 'Compte d\'activité', icon: BadgeEuro },
    { key: 'cras',            href: '/cras',             label: 'CRA',            icon: CalendarCheck },
    { key: 'depenses',        href: '/depenses',         label: 'Notes de frais', icon: Receipt },
    { key: 'notifications',   href: '/notifications',    label: 'Messages',       icon: Bell },
];

const SECONDARY: TabItem[] = [
    { key: 'missions',        href: '/missions',         label: 'Missions',                icon: Briefcase },
    { key: 'salaires',        href: '/finance/salaires', label: 'Salaires & versements',   icon: Wallet },
    { key: 'historique-virements', href: '/historique-virements', label: 'Historique virements', icon: ArrowLeftRight },
    { key: 'avantages',       href: '/avantages',        label: 'Mes avantages',           icon: Gift },
    { key: 'documents',       href: '/documents',        label: 'Documents',               icon: FileText },
    { key: 'aide',            href: '/aide',             label: 'Aide & support',          icon: LifeBuoy },
    { key: 'mon-compte',      href: '/mon-compte',       label: 'Mon profil',              icon: UserCircle2 },
];

function isActive(pathname: string, href: string): boolean {
    if (href === '/') return pathname === '/';
    return pathname === href || pathname.startsWith(href + '/');
}

export default function BottomTabBar() {
    const pathname = usePathname() || '/';
    const auth = useAuthOptional();
    const [moreOpen, setMoreOpen] = useState(false);

    const moreActive = SECONDARY.some(s => isActive(pathname, s.href));

    const handleLogout = async () => {
        try { await fetchExtranet('/api/auth/logout', { method: 'POST' }); } catch { /* noop */ }
        auth?.setUser(null);
        if (typeof window !== 'undefined') {
            // Rechargement complet volontaire : purge les caches React Query
            // et tout état résiduel de la session précédente.
            window.location.assign(new URL('/auth/login', window.location.origin));
        }
    };

    return (
        <>
            <nav
                data-testid="extranet-bottom-tab-bar"
                className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200"
                style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            >
                <div className="flex items-stretch">
                    {PRIMARY.map(tab => {
                        const active = isActive(pathname, tab.href);
                        const Icon = tab.icon;
                        return (
                            <Link
                                key={tab.key}
                                href={tab.href}
                                onClick={dispatchNavSectionClick}
                                data-testid={`tab-${tab.key}`}
                                className="flex-1 min-w-0 flex flex-col items-center justify-center px-0.5 py-2 gap-0.5 min-h-[56px]"
                            >
                                <Icon
                                    className={`w-5 h-5 ${active ? 'text-[#000091]' : 'text-slate-400'}`}
                                    strokeWidth={active ? 2.5 : 2}
                                />
                                <span className={`text-[10px] leading-[1.15] text-center ${active ? 'text-[#000091] font-semibold' : 'text-slate-500'}`}>
                                    {tab.label}
                                </span>
                            </Link>
                        );
                    })}
                    <button
                        onClick={() => setMoreOpen(true)}
                        data-testid="tab-more"
                        className="flex-1 min-w-0 flex flex-col items-center justify-center px-0.5 py-2 gap-0.5 min-h-[56px]"
                    >
                        <MoreHorizontal
                            className={`w-5 h-5 ${moreActive ? 'text-[#000091]' : 'text-slate-400'}`}
                            strokeWidth={moreActive ? 2.5 : 2}
                        />
                        <span className={`text-[10px] leading-tight ${moreActive ? 'text-[#000091] font-semibold' : 'text-slate-500'}`}>
                            Plus
                        </span>
                    </button>
                </div>
            </nav>

            {/* Sheet "Plus" */}
            {moreOpen && (
                <div
                    data-testid="tab-more-sheet"
                    className="lg:hidden fixed inset-0 z-[60] bg-slate-900/40"
                    onClick={() => setMoreOpen(false)}
                >
                    <div
                        className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl"
                        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                            <h2 className="text-[14px] font-bold text-slate-900">Plus</h2>
                            <button
                                onClick={() => setMoreOpen(false)}
                                className="p-1.5 rounded-lg hover:bg-slate-100"
                                aria-label="Fermer"
                            >
                                <X className="w-4 h-4 text-slate-500" />
                            </button>
                        </div>
                        <div className="py-2">
                            {SECONDARY.map(s => {
                                const active = isActive(pathname, s.href);
                                const Icon = s.icon;
                                return (
                                    <Link
                                        key={s.key}
                                        href={s.href}
                                        data-testid={`sheet-${s.key}`}
                                        onClick={() => { setMoreOpen(false); dispatchNavSectionClick(); }}
                                        className={`flex items-center gap-3 px-4 py-3 ${active ? 'bg-[#000091]/8' : 'hover:bg-slate-50'}`}
                                    >
                                        <Icon className={`w-5 h-5 ${active ? 'text-[#000091]' : 'text-slate-400'}`} strokeWidth={2} />
                                        <span className={`text-[14px] ${active ? 'text-[#000091] font-semibold' : 'text-slate-700'}`}>
                                            {s.label}
                                        </span>
                                    </Link>
                                );
                            })}
                            <button
                                onClick={handleLogout}
                                data-testid="sheet-logout"
                                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-rose-50 text-left"
                            >
                                <LogOut className="w-5 h-5 text-slate-400" strokeWidth={2} />
                                <span className="text-[14px] text-slate-700">Déconnexion</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
